import { gql } from '@apollo/client';
import React from 'react';
import I18n from 'i18n-js';
import { withRouter, WithRouterProps } from 'react-router';

import { RCListingCard } from '@reverbdotcom/cadence/components';

import ImageCarousel from './image_carousel';
import {
  ListingCardFields,
  FinancingFields,
  InOtherCartsCardData,
} from '../rql';
import AddToCartButton from './add_to_cart_button';
import { WatchBadgeFragment, WatchBadge, DisplayStyle } from './watch_badge';
import { WatchParentComponentName } from '../event_tracking/watchlist';
import CoreLink from './core_link';
import { ITEM_WITH_SLUG_ROUTE, listingItemPagePath } from '../url_helpers';
import { useUser } from '../user_hooks';
import { shouldShowBumpTag } from './bump_tag';
import { buildListingClickEvent } from '../elog/mparticle_tracker';
import { LISTING_STATES, WEB } from '../constants';
import experiments from '../experiments';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';
import { ListingOfferMetadata } from './listing_offer_metadata';
import CreateOfferButton, { ListingCreateOfferButtonFragment } from '../offers/create_offer_button';

import {
  getEyebrow,
  buildListingForBuyer,
  ListingForBuyerFragment,
  getRCListingCardNudges,
  buildPrice,
  showProximityContent,
  isListingDomesticToBuyer,
} from '../listing_card_helpers';

const DISPLAY_STYLE = 'squareCard';

type Listing = ListingCardFields.Fragment & FinancingFields.Fragment & InOtherCartsCardData.Fragment;

interface IExternalProps {
  // This type is likely _more_ than what this component needs.
  // Cutting a fragment for this component would help better communicate
  // the contract here.
  listing?: Listing;
  position?: number;
  soldListing?: boolean;
  footerContent?: React.ReactNode;
  hideMetadataAsOffer?: boolean;
  trackingName?: string;
  displayAddToCart?: boolean;
  displayMakeAnOffer?: boolean;
  listingsCount?: number;
  combinedShippingDiscount?: boolean;
  trackingQuery?: string;
  trackingFilter?: string;
  trackingSort?: string;
  trackingPage?: number;
  showFlag?: boolean;
  /** Please use a WatchBadgeParentComponent value when possible instead of a plain string! */
  watchBadgeParentComponentName?: WatchParentComponentName | string;
  displayGreatValue?: boolean;
  handleCreateWatchResult?: () => void;
  minimalNudges?: boolean;
  showShippingDisplay?: boolean;
  hideActionsOnMobile?: boolean;
}

type Props = IExternalProps & WithRouterProps;

export function ListingCard({
  listing = undefined,
  location,
  position = undefined,
  soldListing = false,
  footerContent = undefined,
  hideMetadataAsOffer = false,
  trackingName = undefined,
  displayAddToCart = false,
  displayMakeAnOffer = false,
  listingsCount = undefined,
  combinedShippingDiscount = false,
  trackingQuery = undefined,
  trackingFilter = undefined,
  trackingSort = undefined,
  trackingPage = undefined,
  showFlag = false,
  watchBadgeParentComponentName = undefined,
  displayGreatValue = true,
  handleCreateWatchResult = undefined,
  minimalNudges = false,
  showShippingDisplay = false,
  hideActionsOnMobile = false,
}: Props) {

  const user = useUser();

  if (!listing) {
    return <RCListingCard.Skeleton />;
  }

  const decoratedListing = buildListingForBuyer(listing, {
    combinedDiscountActive: combinedShippingDiscount,
  });
  const isBumped = shouldShowBumpTag(listing);

  function listingUrl() {
    if (soldListing) {
      return (
        ITEM_WITH_SLUG_ROUTE.expand({
          slug: listing.slug,
          id: listing.id,
          show_sold: true,
        })
      );
    }

    return (
      listingItemPagePath(listing)
    );
  }

  function renderMetadata() {
    if (hideMetadataAsOffer) return null;

    return (
      <ListingOfferMetadata listing={listing} />
    );
  }

  function clickEvent() {
    const sellerCountryCode = listing.shop.address?.country?.countryCode;

    return {
      experiments: formatExperimentsForEventAttributes(
        user,
        [
          experiments.LISTING_CARD_LOCATION,
          experiments.DUMMY,
          experiments.PERSONALIZED_HOMEPAGE_REDESIGN,
        ],
      ),
      query: trackingQuery,
      filter: trackingFilter,
      sort: trackingSort,
      page: trackingPage,
      shownAsBumped: isBumped,
      ...buildListingClickEvent({
        listing: listing,
        componentName: trackingName,
        displayStyle: DISPLAY_STYLE,
        position,
        listingsCount,
        city: listing.shop.address?.locality,
        region: listing.shop.address?.region,
        countryCode: sellerCountryCode,
        isListingDomesticToBuyer: isListingDomesticToBuyer(sellerCountryCode, user?.countryCode),
      }),
    };
  }

  function shopLocation() {
    const shopAddress = listing.shop?.address;
    if (!shopAddress) return '';

    if (shopAddress?.displayLocation) {
      return shopAddress.displayLocation;
    }

    if (showProximityContent(shopAddress, user, location) && shopAddress?.locality && shopAddress?.region) {
      return I18n.t('commons.listingCard.location.sameCountryUs', {
        city: shopAddress.locality, state: shopAddress.region,
      });
    }

    return '';
  }

  const openNewTab = user && user.deviceName === WEB;
  const images = listing.images.map(i => (i.source));
  const violatorText = listing.state !== LISTING_STATES.LIVE ? listing.stateDescription : null;

  return (
    <>
      <RCListingCard
        linkElement={(children) => (
          <CoreLink
            to={listingUrl()}
            target={openNewTab ? '_blank' : undefined}
            clickEvent={clickEvent()}
          >
            {children}
          </CoreLink>
        )}
        metaElement={renderMetadata()}
        thumbnailElement={(<ImageCarousel images={images} />)}
        title={listing.title}
        conditionLabel={decoratedListing.conditionLabel}
        shopLocation={shopLocation()}
        price={buildPrice(decoratedListing, combinedShippingDiscount, showShippingDisplay)}
        nudges={getRCListingCardNudges({ listing, decoratedListing, shop: listing.shop, displayGreatValue, user, showFlag, location, minimalNudges })}
        fullHeight
        eyebrow={getEyebrow(listing)}
        favoriteButtonElement={(
          <WatchBadge
            listing={listing}
            displayStyle={DisplayStyle.RC_FAVORITE_BUTTON}
            parentComponentName={watchBadgeParentComponentName || trackingName}
            handleCreateWatchResult={handleCreateWatchResult}
            useToast
          />
        )}
        violatorText={violatorText}
        hideActionsOnMobile={hideActionsOnMobile}
        primaryAction={displayAddToCart &&
          <AddToCartButton
            listing={listing}
            position={position}
            parentComponentName={trackingName}
          />
        }
        secondaryAction={displayMakeAnOffer &&
          <CreateOfferButton
            listing={listing}
            parentComponentName={trackingName}
          />
        }
      />
      {footerContent}
    </>
  );
}

export const ListingCardFragment = gql`
  fragment ListingCardFields on Listing {
    _id
    ...ListingForBuyerFields
    ...WatchBadgeData
    ...ListingCreateOfferButtonData
  }
  ${ListingForBuyerFragment}
  ${ListingCreateOfferButtonFragment}
  ${WatchBadgeFragment}
`;

export default withRouter(ListingCard);
